import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { LiveClock } from '../../components/LiveClock';
import { PageHeader } from '../../components/PageHeader';
import {
  CalendarDays, List as ListIcon, UserCircle, PlusCircle,
  PartyPopper, User, MessageCircle, Phone, Pin, Mail,
  Check, X as XIcon,
} from 'lucide-react';
import { reservasApi, sucursalesApi, Sucursal, eventosApi } from '../../lib/api';
import { NuevaReservaModal } from '../../components/NuevaReservaModal';
import { Footer } from '../../components/footer';
import { useInicio } from "../../lib/permisos";
import { useAuth } from "../../lib/auth";
import { Users, CheckCircle2, Clock, ChevronRight, UserCheck, UserX } from 'lucide-react';

const ESTADO_COLORS: Record<string, string> = {
  'pendiente':   'bg-amber-100 text-amber-800 border-amber-300',
  'confirmada':  'bg-emerald-100 text-emerald-800 border-emerald-300',
  'cancelada':   'bg-rose-100 text-rose-800 border-rose-300',
  'no_show':     'bg-slate-200 text-slate-700 border-slate-400',
  'atendida':    'bg-blue-100 text-blue-800 border-blue-300',
};

const ESTADO_LABELS: Record<string, string> = {
  'pendiente':  'Pendiente',
  'confirmada': 'Confirmada',
  'cancelada':  'Cancelada',
  'no_show':    'No-show',
  'atendida':   'Atendida',
};

function fmtFechaHora(fecha: string, hora: string) {
  const d = new Date(`${fecha}T${hora}`);
  return d.toLocaleString('es-VE', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function hoyISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function ReservasList() {
  const inicio = useInicio();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'pendientes';
  const search = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(search);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pendientesCount, setPendientesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();
  const navigate = useNavigate();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [accionando, setAccionando] = useState<number | null>(null);
  const [enviandoEmail, setEnviandoEmail] = useState<number | null>(null);
  const [eventosHoy, setEventosHoy] = useState<any[]>([]);
  const [eventoExpandido, setEventoExpandido] = useState<number | null>(null);
  const [eventoDetalle, setEventoDetalle] = useState<any | null>(null);

  // Filtros nuevos
  const [sucursal, setSucursal] = useState<number | undefined>(undefined);
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');

  // Sucursales activas (desde la base)
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  useEffect(() => {
    sucursalesApi.list().then(setSucursales).catch(() => setSucursales([]));
  }, []);
  const SUCURSAL_NOMBRE: Record<number, string> = Object.fromEntries(
    sucursales.map((s) => [s.id, s.nombre]),
  );

  const filtros = useMemo(() => {
    const hoy = hoyISO();
    let base: any = {};
    if (tab === 'pendientes') base = { estado: 'pendiente' };
    else if (tab === 'hoy') base = { desde: hoy, hasta: hoy };
    else if (tab === 'proximas') base = { desde: hoy, estado: 'confirmada' };
    else if (tab === 'historico') base = { hasta: hoy };
    // Si el usuario fija fechas explícitas, mandan ellas (sobreescriben las del tab)
    if (fDesde) base.desde = fDesde;
    if (fHasta) base.hasta = fHasta;
    return base;
  }, [tab, fDesde, fHasta]);

  // Cargar eventos de hoy cuando estamos en tab=hoy
  useEffect(() => {
    if (tab !== 'hoy' || !token) return;
    const hoy = hoyISO();
    eventosApi.list({ desde: hoy, hasta: hoy })
      .then(setEventosHoy).catch(() => setEventosHoy([]));
  }, [tab, token, recarga]);

  const abrirEvento = async (id: number) => {
    const data = await eventosApi.get(id);
    setEventoDetalle(data);
    setEventoExpandido(id);
  };

  const checkInInvitado = async (invId: number, eventoId: number) => {
    await eventosApi.checkIn(invId);
    await abrirEvento(eventoId);
    setRecarga(r => r + 1);
  };

  const noShowInvitado = async (invId: number, eventoId: number) => {
    await eventosApi.noShow(invId);
    await abrirEvento(eventoId);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      reservasApi.list({ ...filtros, search, limit: 100 }),
      reservasApi.countPendientes(),
    ])
      .then(([r, p]) => {
        if (cancelled) return;
        setItems(r.items ?? []);
        setTotal(r.total ?? 0);
        setPendientesCount(p.count);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filtros, search, recarga]);

  // Filtro de sucursal en el frontend (la query del backend no filtra por sucursal)
  const visibles = useMemo(
    () => (sucursal === undefined ? items : items.filter((r) => r.sucursalId === sucursal)),
    [items, sucursal],
  );

  function setTab(t: string) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next);
  }

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput) next.set('search', searchInput);
    else next.delete('search');
    setSearchParams(next);
  }

  function ponerHoy() {
    const h = hoyISO();
    setFDesde(h);
    setFHasta(h);
  }

  function limpiarFechas() {
    setFDesde('');
    setFHasta('');
  }

  const esHoy = tab === 'hoy';

  // Stats para la vista "Hoy"
  const stats = useMemo(() => {
    if (!esHoy) return null;
    return {
      pendientes: items.filter(r => r.estado === 'pendiente').length,
      confirmadas: items.filter(r => r.estado === 'confirmada').length,
      canceladas: items.filter(r => r.estado === 'cancelada').length,
      total: items.length,
    };
  }, [items, esHoy]);

  async function confirmar(id: number) {
    setAccionando(id);
    try {
      await reservasApi.confirmar(id);
      setRecarga(r => r + 1);
    } finally { setAccionando(null); }
  }

  async function cancelar(id: number, nombre: string) {
    if (!confirm(`¿Cancelar la reserva de ${nombre}?`)) return;
    setAccionando(id);
    try {
      await reservasApi.cancelar(id, 'Cancelada por el establecimiento');
      setRecarga(r => r + 1);
    } finally { setAccionando(null); }
  }

  async function enviarRecordatorio(id: number, email: string) {
    if (!confirm(`¿Enviar recordatorio de confirmación a ${email}?`)) return;
    setEnviandoEmail(id);
    try {
      const res = await fetch(`/api/v1/reservas/${id}/enviar-recordatorio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) alert(data.mensaje);
      else alert(`Error: ${data.mensaje ?? 'No se pudo enviar'}`);
    } catch { alert('Error al enviar el recordatorio'); }
    finally { setEnviandoEmail(null); }
  }

  function waLink(r: any) {
    const num = (r.telefono ?? '').replace(/\D/g, '');
    if (!num) return null;
    const msg = encodeURIComponent(`Hola ${r.nombreCliente}! Te recordamos tu reserva #${r.id} en ${r.sucursalId ? (SUCURSAL_NOMBRE[r.sucursalId] ?? '') : ''} hoy a las ${r.horaReserva}.`);
    return `https://wa.me/${num}?text=${msg}`;
  }

  function emailLink(r: any) {
    if (!r.email) return null;
    const subject = encodeURIComponent(`Recordatorio reserva #${r.id}`);
    const body = encodeURIComponent(`Hola ${r.nombreCliente},\n\nTe recordamos tu reserva hoy a las ${r.horaReserva}.\n\nSaludos.`);
    return `mailto:${r.email}?subject=${subject}&body=${body}`;
  }

  const TABS = [
    { key: 'pendientes', label: 'Pendientes', badge: pendientesCount },
    { key: 'hoy',        label: 'Hoy' },
    { key: 'proximas',   label: 'Próximas' },
    { key: 'historico',  label: 'Histórico' },
  ];

  const hayFiltroFecha = !!(fDesde || fHasta);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <PageHeader
        crumbs={[{ label: "Inicio", to: inicio }, { label: "Reservas" }]}
        icon={ListIcon}
        actions={[
          { label: "Calendario", icon: CalendarDays, to: "/calendario", variant: "ghost" },
          { label: "Clientes", icon: UserCircle, to: "/customers", variant: "ghost" },
          { label: "Nueva reserva", icon: PlusCircle, onClick: () => setModalAbierto(true), variant: "primary", badge: pendientesCount > 0 ? pendientesCount : undefined },
        ]}
      />

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-6">
        {/* Tabs (sticky para no perder contexto al scrollear la lista) */}
        <div className="sticky top-0 z-20 -mx-6 px-6 pt-2 pb-3 bg-slate-50/95 backdrop-blur border-b border-slate-200">
        <div className="bg-white rounded-lg border border-slate-200 flex flex-wrap gap-1 p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
                tab === t.key ? 'bg-brand-700 text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {t.label}
              {t.badge !== undefined && t.badge > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  tab === t.key ? 'bg-white text-brand-700' : 'bg-amber-100 text-amber-800'
                }`}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        </div>

        {/* Filtros: sucursal + rango de fechas */}
        <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4 mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Sucursal</label>
            <div className="flex flex-wrap bg-slate-50 rounded-lg border border-slate-200 p-1">
              <button
                onClick={() => setSucursal(undefined)}
                className={`px-3 py-1.5 text-sm rounded-md font-medium ${sucursal === undefined ? 'bg-brand-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                Todas
              </button>
              {sucursales.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSucursal(s.id)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium ${sucursal === s.id ? 'bg-brand-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {s.nombre}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">Desde</label>
            <input
              type="date"
              value={fDesde}
              onChange={(e) => setFDesde(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Hasta</label>
            <input
              type="date"
              value={fHasta}
              onChange={(e) => setFHasta(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={ponerHoy}
              className="px-3 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 font-medium">
              Hoy
            </button>
            {hayFiltroFecha && (
              <button
                onClick={limpiarFechas}
                className="px-3 py-2 text-sm rounded-md border border-slate-300 text-slate-500 hover:bg-slate-100">
                Limpiar fechas
              </button>
            )}
          </div>
        </div>

        {/* ── EVENTOS PRIVADOS DEL DÍA ── */}
        {esHoy && eventosHoy.length > 0 && (
          <div className="mb-4 space-y-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <Users className="size-3.5" /> Eventos del día ({eventosHoy.length})
            </h3>
            {eventosHoy.map(ev => {
              const isOpen = eventoExpandido === ev.id;
              // Cuando el evento está abierto usamos el detalle (más fresco tras un check-in);
              // si no, usamos los invitados del list (que ya vienen via leftJoin).
              const det = isOpen ? eventoDetalle : ev;
              const invitados = det?.invitados ?? [];
              const llegaron = invitados.filter((i: any) => i.estado === 'llegó').length;
              const pendientes = invitados.filter((i: any) => i.estado === 'pendiente').length;
              const total = invitados.length;
              // Personas reales = invitado + sus acompañantes
              const personasLlegadas = invitados
                .filter((i: any) => i.estado === 'llegó')
                .reduce((a: number, i: any) => a + 1 + (i.acompanantes ?? 0), 0);
              const totalPersonas = invitados.reduce((a: number, i: any) => a + 1 + (i.acompanantes ?? 0), 0)
                || ev.totalInvitados || 0;
              const pct = totalPersonas > 0 ? Math.round((personasLlegadas / totalPersonas) * 100) : 0;

              return (
                <div key={ev.id}
                  className="bg-indigo-50 border border-indigo-200 rounded-xl overflow-hidden">
                  {/* Header del evento */}
                  <button
                    onClick={() => {
                      if (isOpen) { setEventoExpandido(null); setEventoDetalle(null); }
                      else abrirEvento(ev.id);
                    }}
                    className="w-full flex items-center gap-4 px-4 py-3 hover:bg-indigo-100 transition-colors text-left"
                  >
                    <div className="size-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shrink-0">
                      <PartyPopper size={18} aria-hidden />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-indigo-900">{ev.nombre}</p>
                      <div className="flex items-center gap-3 text-xs text-indigo-600 flex-wrap mt-0.5">
                        <span className="flex items-center gap-1"><Clock className="size-3" /> {ev.horaInicio}{ev.horaFin ? ` – ${ev.horaFin}` : ''}</span>
                        <span className="flex items-center gap-1"><Users className="size-3" /> {ev.totalInvitados} personas</span>
                        {ev.organizadorNombre && (
                          <span className="inline-flex items-center gap-1"><User size={12} aria-hidden /> {ev.organizadorNombre}</span>
                        )}
                        {totalPersonas > 0 && (
                          <span className="font-semibold text-emerald-700">
                            {personasLlegadas}/{totalPersonas} llegaron
                            {total > 0 && total !== totalPersonas && (
                              <span className="font-normal text-emerald-600/70"> · {llegaron}/{total} invitados</span>
                            )}
                          </span>
                        )}
                      </div>
                      {/* Barra de progreso siempre visible si hay invitados/personas */}
                      {totalPersonas > 0 && (
                        <div className="mt-2 max-w-xs">
                          <div className="h-2 bg-indigo-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full transition-[width] duration-500 ease-out"
                              style={{ width: `${pct}%` }}
                              role="progressbar"
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={pct}
                              aria-label={`${personasLlegadas} de ${totalPersonas} personas llegaron`}
                            />
                          </div>
                          <span className="text-[11px] text-indigo-500 mt-0.5 inline-block">{pct}% asistencia</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link
                        to="/tienda/eventos"
                        onClick={e => e.stopPropagation()}
                        className="text-xs text-indigo-600 hover:underline px-2 py-1 rounded bg-indigo-100"
                      >
                        Ver detalle
                      </Link>
                      <ChevronRight className={`size-4 text-indigo-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    </div>
                  </button>

                  {/* Lista de invitados expandida */}
                  {isOpen && (
                    <div className="border-t border-indigo-200 divide-y divide-indigo-100 max-h-80 overflow-y-auto">
                      {invitados.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-indigo-400">Sin invitados cargados</p>
                      ) : (
                        <>
                          {/* Pendientes */}
                          {invitados.filter((i: any) => i.estado === 'pendiente').map((inv: any) => (
                            <div key={inv.id}
                              className="flex items-center gap-3 px-4 py-2.5 bg-white hover:bg-slate-50">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-slate-800">{inv.nombre}</span>
                                {(inv.acompanantes ?? 0) > 0 && (
                                  <span className="ml-2 text-xs text-indigo-500">+{inv.acompanantes} acomp.</span>
                                )}
                                {inv.notas && <span className="ml-2 text-xs text-slate-400 italic">{inv.notas}</span>}
                              </div>
                              <div className="flex gap-1.5 shrink-0">
                                {inv.telefono && (
                                  <a href={`https://wa.me/${inv.telefono.replace(/\D/g,'')}`}
                                    target="_blank" rel="noreferrer"
                                    aria-label={`WhatsApp ${inv.telefono}`}
                                    className="inline-flex items-center text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100">
                                    <MessageCircle size={13} aria-hidden />
                                  </a>
                                )}
                                <button
                                  onClick={() => checkInInvitado(inv.id, ev.id)}
                                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700">
                                  <UserCheck className="size-3" /> Llegó
                                </button>
                                <button
                                  onClick={() => noShowInvitado(inv.id, ev.id)}
                                  className="text-xs px-2 py-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">
                                  No vino
                                </button>
                              </div>
                            </div>
                          ))}
                          {/* Llegados */}
                          {invitados.filter((i: any) => i.estado === 'llegó').map((inv: any) => (
                            <div key={inv.id}
                              className="flex items-center gap-3 px-4 py-2 bg-emerald-50">
                              <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                              <span className="flex-1 text-sm text-emerald-800 font-medium">{inv.nombre}</span>
                              {inv.llegadaAt && (
                                <span className="text-xs text-emerald-600">
                                  {new Date(inv.llegadaAt).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                              <button
                                onClick={() => { eventosApi.undoCheckIn(inv.id).then(() => abrirEvento(ev.id)); }}
                                className="text-xs text-emerald-500 hover:underline px-1">
                                deshacer
                              </button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Stats para la vista Hoy */}
        {esHoy && stats && (
          <div className="grid grid-cols-2 tab:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Total', value: stats.total, color: 'text-slate-700', bg: 'bg-white' },
              { label: 'Pendientes', value: stats.pendientes, color: 'text-amber-700', bg: 'bg-amber-50' },
              { label: 'Confirmadas', value: stats.confirmadas, color: 'text-emerald-700', bg: 'bg-emerald-50' },
              { label: 'Canceladas', value: stats.canceladas, color: 'text-rose-600', bg: 'bg-rose-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} border border-slate-200 rounded-xl p-4 text-center`}>
                <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Buscador */}
        <form onSubmit={onSearchSubmit} className="bg-white rounded-lg border border-slate-200 p-3 mb-4 flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por nombre, teléfono o email…"
            className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <button type="submit" className="px-4 py-2 bg-brand-700 text-white text-sm rounded-md hover:bg-brand-800">
            Buscar
          </button>
        </form>

        {/* Tabla */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {error && (
            <div className="p-4 bg-rose-50 text-rose-800 text-sm border-b border-rose-200">Error: {error}</div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-3 font-semibold text-sm">Hora</th>
                <th className="px-3 py-3 font-semibold text-sm">Cliente</th>
                {!esHoy && <th className="px-3 py-3 font-semibold text-sm hidden lg:table-cell">Sucursal</th>}
                <th className="px-3 py-3 font-semibold text-sm hidden sm:table-cell">Contacto</th>
                <th className="px-3 py-3 font-semibold text-sm text-center hidden sm:table-cell">Pers.</th>
                {!esHoy && <th className="px-3 py-3 font-semibold text-sm hidden lg:table-cell">Tipo</th>}
                <th className="px-3 py-3 font-semibold text-sm">Estado</th>
                {esHoy && <th className="px-3 py-3 font-semibold text-sm hidden sm:table-cell">Acciones</th>}
                {!esHoy && <th className="px-3 py-3 font-semibold text-sm hidden md:table-cell">Origen</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Cargando…</td></tr>
              )}
              {!loading && visibles.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Sin reservas en esta vista</td></tr>
              )}
              {!loading && visibles.map((r) => {
                const wa = waLink(r);
                const mail = emailLink(r);
                const enAccion = accionando === r.id;
                const enEmail = enviandoEmail === r.id;
                return (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${esHoy && r.estado === 'pendiente' ? 'bg-amber-50/40' : ''}`}>
                    {/* Hora */}
                    <td className="px-3 py-3 text-slate-900 align-top">
                      <Link to={`/reservas/${r.id}`} className="text-brand-700 hover:underline font-medium text-sm">
                        {esHoy ? r.horaReserva?.slice(0,5) : fmtFechaHora(r.fechaReserva, r.horaReserva)}
                      </Link>
                      {esHoy && <p className="text-xs text-slate-400">{SUCURSAL_NOMBRE[r.sucursalId] ?? ''}</p>}
                      {/* Personas y origen visibles en móvil */}
                      <div className="sm:hidden mt-0.5 flex gap-2 text-[10px] text-slate-400">
                        <span>{r.numPersonas}p</span>
                        {!esHoy && r.origen && <span>{r.origen}</span>}
                      </div>
                    </td>
                    {/* Cliente — en móvil también muestra teléfono */}
                    <td className="px-3 py-3 text-slate-900 align-top">
                      <span className="font-medium text-sm">{r.nombreCliente}</span>
                      {r.telefono && (
                        <p className="sm:hidden text-xs text-green-700 mt-0.5">
                          {wa
                            ? <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1"><MessageCircle size={12} aria-hidden /> {r.telefono}</a>
                            : <span className="inline-flex items-center gap-1"><Phone size={12} aria-hidden /> {r.telefono}</span>}
                        </p>
                      )}
                      {/* Estado en móvil */}
                      <div className="sm:hidden mt-1">
                        <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full border ${ESTADO_COLORS[r.estado] ?? ''}`}>
                          {ESTADO_LABELS[r.estado] ?? r.estado}
                        </span>
                      </div>
                      {r.requerimientosEspeciales && (
                        <p className="text-xs text-slate-400 truncate max-w-[150px] inline-flex items-center gap-1" title={r.requerimientosEspeciales}>
                          <Pin size={11} aria-hidden className="shrink-0" /> {r.requerimientosEspeciales}
                        </p>
                      )}
                    </td>
                    {!esHoy && (
                      <td className="px-3 py-3 text-slate-600 text-sm hidden lg:table-cell">
                        {r.sucursalId ? (SUCURSAL_NOMBRE[r.sucursalId] ?? `Suc. ${r.sucursalId}`) : '—'}
                      </td>
                    )}
                    <td className="px-3 py-3 hidden sm:table-cell">
                      <div className="flex flex-col gap-1">
                        {r.telefono && (
                          wa
                            ? <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline font-medium"><MessageCircle size={12} aria-hidden /> {r.telefono}</a>
                            : <span className="text-xs text-slate-600 inline-flex items-center gap-1"><Phone size={11} aria-hidden /> {r.telefono}</span>
                        )}
                        {r.email ? (
                          <div className="flex items-center gap-1.5">
                            <a href={`mailto:${r.email}`}
                              className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline truncate max-w-[150px]">
                              <Mail size={11} aria-hidden /> <span className="truncate">{r.email}</span>
                            </a>
                            {r.estado === 'pendiente' && (
                              <button
                                onClick={() => enviarRecordatorio(r.id, r.email)}
                                disabled={enviandoEmail === r.id}
                                title="Enviar link de confirmación por email"
                                className="shrink-0 text-[10px] px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-600 rounded hover:bg-blue-100 disabled:opacity-50"
                              >
                                {enviandoEmail === r.id ? '…' : 'Enviar link'}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300 italic">sin email</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center tabular-nums text-sm hidden sm:table-cell">{r.numPersonas}</td>
                    {!esHoy && <td className="px-3 py-3 text-slate-600 text-xs hidden lg:table-cell">{r.tipoEvento || '—'}</td>}
                    {/* Estado — oculto en móvil (se muestra en columna Cliente) */}
                    <td className="px-3 py-3 hidden sm:table-cell">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${ESTADO_COLORS[r.estado] ?? ''}`}>
                        {ESTADO_LABELS[r.estado] ?? r.estado}
                      </span>
                    </td>
                    {esHoy && (
                      <td className="px-3 py-2 hidden sm:table-cell">
                        <div className="flex flex-wrap gap-1.5">
                          {r.estado === 'pendiente' && (
                            <button
                              onClick={() => confirmar(r.id)}
                              disabled={enAccion}
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium">
                              {enAccion
                                ? '…'
                                : <><Check size={12} aria-hidden /> Confirmar</>}
                            </button>
                          )}
                          {(r.estado === 'pendiente' || r.estado === 'confirmada') && (
                            <button
                              onClick={() => cancelar(r.id, r.nombreCliente)}
                              disabled={enAccion}
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-white border border-rose-300 text-rose-600 rounded-lg hover:bg-rose-50 disabled:opacity-50">
                              <XIcon size={12} aria-hidden /> Cancelar
                            </button>
                          )}
                          {r.estado === 'pendiente' && r.email && (
                            <button
                              onClick={() => enviarRecordatorio(r.id, r.email)}
                              disabled={enEmail}
                              aria-label={`Enviar recordatorio por email a ${r.email}`}
                              title={`Enviar recordatorio a ${r.email}`}
                              className="inline-flex items-center text-xs px-2.5 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50">
                              {enEmail ? '…' : <Mail size={13} aria-hidden />}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    {!esHoy && <td className="px-3 py-3 text-xs text-slate-500 hidden md:table-cell">{r.origen}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-600">
            {!loading && (
              sucursal === undefined
                ? (total > 0 ? `Total: ${total} reservas` : '')
                : `${visibles.length} reserva${visibles.length !== 1 ? 's' : ''} en ${SUCURSAL_NOMBRE[sucursal]} (de ${items.length} cargadas)`
            )}
          </div>
        </div>
      </main>
      {modalAbierto && (
        <NuevaReservaModal
          onClose={() => setModalAbierto(false)}
          onCreated={() => setRecarga((r) => r + 1)}
        />
      )}
      <Footer />
    </div>
  );
}
